/**
 * Activity Backup Service
 * 
 * Provides functionality to backup recent activity data to a JSON file
 * for easy access and backup by scripts.
 */

export interface SentRequest {
  id: string;
  templateName: string;
  recipient: string;
  documents: number;
  sentAt: string;
  status: 'Sent' | 'Viewed' | 'Completed';
  sentBy?: string;
  templateId?: string;
  actionType?: 'sent' | 'reminder';
}

const STORAGE_KEY = 'sentRequests';

/**
 * Get all activity from localStorage
 */
export const getActivityFromStorage = (): SentRequest[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading activity from localStorage:', error);
    return [];
  }
};

/**
 * Save activity to localStorage
 */
export const saveActivityToStorage = (activity: SentRequest[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(activity));
  } catch (error) {
    console.error('Error saving activity to localStorage:', error);
  }
};

/**
 * Export activity data to a downloadable JSON file
 * This allows users to manually backup their activity data
 */
export const exportActivityToFile = (): void => {
  const activity = getActivityFromStorage();
  const dataStr = JSON.stringify(activity, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `activity-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Automatically backup activity to public directory (server-side)
 * Note: In a browser environment, we can't directly write to the file system.
 * This function prepares the data for server-side backup scripts.
 * 
 * For actual file system writes, you would need a backend API endpoint that:
 * 1. Accepts this JSON data
 * 2. Writes it to a writable directory on the server
 * 3. Returns success/failure status
 */
export const prepareBackupData = (): {
  data: SentRequest[];
  filename: string;
  timestamp: string;
} => {
  const activity = getActivityFromStorage();
  const timestamp = new Date().toISOString();
  const filename = `activity-backup-${timestamp.split('T')[0]}.json`;
  
  return {
    data: activity,
    filename,
    timestamp
  };
};

/**
 * Send activity data to a backup API endpoint (if available)
 * This would be called periodically or on certain triggers
 */
export const sendActivityToBackupAPI = async (apiEndpoint: string): Promise<boolean> => {
  try {
    const backup = prepareBackupData();
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backup),
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error sending activity to backup API:', error);
    return false;
  }
};

/**
 * Import activity from a JSON file
 * Useful for restoring backups
 */
export const importActivityFromFile = (file: File): Promise<SentRequest[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const activity = JSON.parse(content) as SentRequest[];
        
        // Validate the data structure
        if (!Array.isArray(activity)) {
          reject(new Error('Invalid backup file format'));
          return;
        }
        
        // Merge with existing data (or replace)
        const existing = getActivityFromStorage();
        const merged = [...existing, ...activity];
        
        // Remove duplicates based on ID
        const uniqueActivity = Array.from(
          new Map(merged.map(item => [item.id, item])).values()
        );
        
        saveActivityToStorage(uniqueActivity);
        resolve(uniqueActivity);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

/**
 * Schedule automatic backups (client-side)
 * This creates a backup every specified interval.
 * 
 * WARNING: Automatic downloads may be blocked by browsers without user interaction.
 * This is primarily useful for development/testing. For production, consider:
 * 1. Using a backend API to store backups
 * 2. Implementing user-triggered manual backups
 * 3. Using browser notifications to prompt users for backup
 * 
 * @param intervalHours - Hours between automatic backups (default: 24)
 * @returns Cleanup function to clear the interval
 */
export const scheduleAutoBackup = (intervalHours: number = 24): () => void => {
  const intervalMs = intervalHours * 60 * 60 * 1000;
  
  const intervalId = setInterval(() => {
    console.log('Auto-backup triggered');
    try {
      // Trigger download (user may need to approve based on browser settings)
      exportActivityToFile();
    } catch (error) {
      console.error('Auto-backup failed:', error);
      // In production, you might want to notify the user or send to an API instead
    }
  }, intervalMs);
  
  // Return cleanup function
  return () => clearInterval(intervalId);
};
